import {LitElement, html, css} from 'lit';
import {customElement} from 'lit/decorators/custom-element.js';
import {connect} from 'pwa-helpers';
import {choose} from 'lit/directives/choose.js';
import {msg} from '@lit/localize';
import store from '../../store';
import {
	selectIomoAllPitch, selectIomoAllRoll,
	selectIomoDesign,
	selectIomoLayout,
	selectIomoLayoutIsSatisfied,
	selectIomoMaxAngle,
	selectIomoMaxHeight,
	selectIomoMinAngle,
	selectIomoMinHeight,
	selectIomoOffsetFromBottom,
	selectIomoOffsetFromTop,
	selectIomoPoleHeight
} from '../iomoSelectors';

@customElement('iomo-layout-settings')
export class IomoLayoutSettings extends connect(store)(LitElement) {

	static styles = [
		css`
			:host {
				display: flex;
				flex-flow: column;
				width: 100%;
				break-inside: avoid;
				padding-bottom: var(--large-space);
				place-content: flex-start;
				gap: var(--large-space);
			}

			.info {
				font: var(--font-widget-label);
				color: var(--color-widget-text);
			}
		`
	];

	constructor() {
		super();
		store.subscribe(() => this.requestUpdate());
	}

	stateChanged(state) {
		this.state = state;
		this.lang = state.status.lang;
		this.layout = selectIomoLayout(state);
		this.system = selectIomoDesign(state);
		this.maxHeadHeight = selectIomoPoleHeight(state) - selectIomoOffsetFromTop(state);
		this.minHeadHeight = Math.min(this.maxHeadHeight, selectIomoOffsetFromBottom(state));

		this.iomoLayoutOptions = [
			{
				id: 'spiral',
				label: {
					de: 'Spirale',
					en: 'Spiral',
					fr: 'Spiral',
				},
				description: {
					de: 'Id deserunt do eiusmod voluptate cillum dolor est laboris nulla consectetur cupidatat nostrud cillum deserunt. Veniam duis aliqua laboris duis consequat tempor laboris ea occaecat laborum est qui.',
					en: 'Id deserunt do eiusmod voluptate cillum dolor est laboris nulla consectetur cupidatat nostrud cillum deserunt. Veniam duis aliqua laboris duis consequat tempor laboris ea occaecat laborum est qui.',
					fr: 'Id deserunt do eiusmod voluptate cillum dolor est laboris nulla consectetur cupidatat nostrud cillum deserunt. Veniam duis aliqua laboris duis consequat tempor laboris ea occaecat laborum est qui.',
				}
			},
			{
				id: 'linear',
				label: {
					de: 'Linear',
					en: 'Linear',
					fr: 'Linear',
				},
				description: {
					de: 'Id deserunt do eiusmod voluptate cillum dolor est laboris nulla consectetur cupidatat nostrud cillum deserunt. Veniam duis aliqua laboris duis consequat tempor laboris ea occaecat laborum est qui.',
					en: 'Id deserunt do eiusmod voluptate cillum dolor est laboris nulla consectetur cupidatat nostrud cillum deserunt. Veniam duis aliqua laboris duis consequat tempor laboris ea occaecat laborum est qui.',
					fr: 'Id deserunt do eiusmod voluptate cillum dolor est laboris nulla consectetur cupidatat nostrud cillum deserunt. Veniam duis aliqua laboris duis consequat tempor laboris ea occaecat laborum est qui.',
				}
			},
			{
				id: 'cluster',
				label: {
					de: 'Cluster',
					en: 'Cluster',
					fr: 'Cluster',
				},
				description: {
					de: 'Id deserunt do eiusmod voluptate cillum dolor est laboris nulla consectetur cupidatat nostrud cillum deserunt. Veniam duis aliqua laboris duis consequat tempor laboris ea occaecat laborum est qui.',
					en: 'Id deserunt do eiusmod voluptate cillum dolor est laboris nulla consectetur cupidatat nostrud cillum deserunt. Veniam duis aliqua laboris duis consequat tempor laboris ea occaecat laborum est qui.',
					fr: 'Id deserunt do eiusmod voluptate cillum dolor est laboris nulla consectetur cupidatat nostrud cillum deserunt. Veniam duis aliqua laboris duis consequat tempor laboris ea occaecat laborum est qui.',
				},
			},
			{
				id: 'custom',
				label: {
					de: 'Mixed',
					en: 'mixed',
					fr: 'mixed',
				},
				description: {
					de: 'Id deserunt do eiusmod voluptate cillum dolor est laboris nulla consectetur cupidatat nostrud cillum deserunt. Veniam duis aliqua laboris duis consequat tempor laboris ea occaecat laborum est qui.',
					en: 'Id deserunt do eiusmod voluptate cillum dolor est laboris nulla consectetur cupidatat nostrud cillum deserunt. Veniam duis aliqua laboris duis consequat tempor laboris ea occaecat laborum est qui.',
					fr: 'Id deserunt do eiusmod voluptate cillum dolor est laboris nulla consectetur cupidatat nostrud cillum deserunt. Veniam duis aliqua laboris duis consequat tempor laboris ea occaecat laborum est qui.',
				},
			},
		];

	}

	layoutCanBeSelected(state, id) {
		if (id === 'custom') {
			return false;
		}
		if (id === 'cluster') {
			return true;
		}
		return true;
	}

	render() {
		return html`
			<list-select
				.title=${{de: 'Layout', en: 'Layout', fr: 'disposition'}}
				.options=${this.iomoLayoutOptions}
				action='iomo/setLayout'
				.selected=${selectIomoLayout}
				.canBeSelected=${this.layoutCanBeSelected}
				.isSatisfied=${selectIomoLayoutIsSatisfied}>
			</list-select>
			${choose(
				this.layout,
				[
					['custom', () => html`
						<div class="info">
							${msg('One or more selected heads where edited. Selecting a preset layout will override the individual settings.')}
						</div>
					`],
					['spiral', () => html`
						<numeric-range
							.title=${{
								de: 'Lichtpunkt Höhen (min - max)',
								en: 'Luminaire Heights (min - max)',
								fr: 'hauteurs des luminaires (min - max)'
							}}
							max="${this.maxHeadHeight}"
							min="${this.minHeadHeight}"
							step="0.1"
							digits="2"
							unit="m"

							action='iomo/setMaxHeight'
							.selected=${selectIomoMaxHeight}

							.twoValues=${true}
							action2='iomo/setMinHeight'
							.selected2=${selectIomoMinHeight}

							.isSatisfied=${() => true}
						>
						</numeric-range>
						<numeric-range
							.title=${{
								de: 'Lichtpunkt Winkel (min - max)',
								en: 'Luminaires Angle (min - max)',
								fr: 'angle des luminaires (min - max)',
							}}
							max="360"
							min="0"
							step="1"
							digits="0"
							unit="°"

							.isSatisfied=${() => true}

							action='iomo/setMaxAngle'
							.selected=${selectIomoMaxAngle}

							.twoValues=${true}
							action2='iomo/setMinAngle'
							.selected2=${selectIomoMinAngle}
						>
						</numeric-range>
						<numeric-range
							.title=${{
								de: 'Roll',
								en: 'Roll',
								fr: 'Roll'
							}}
							max="360" min="0" step="0.1" digits="1"
							action='iomo/setAllRoll'
							.selected=${selectIomoAllRoll}
							.isSatisfied=${() => true}>
						</numeric-range>

						<numeric-range
							.title=${{
								de: 'Neigung',
								en: 'pitch',
								fr: 'Pitch'
							}}
							max="180" min="0" step="0.1" digits="1"
							action='iomo/setAllPitch'
							.selected=${selectIomoAllPitch}
							.isSatisfied=${() => true}>
						</numeric-range>
					`],
					['linear', () => html`
						<numeric-range
							.title=${{
								de: 'Lichtpunkt Höhen (min - max)',
								en: 'Luminaire Heights (min - max)',
								fr: 'hauteurs des luminaires (min - max)'
							}}
							max="${this.maxHeadHeight}"
							min="${this.minHeadHeight}"
							step="0.1"
							digits="2"
							unit="m"

							action='iomo/setMaxHeight'
							.selected=${selectIomoMaxHeight}

							.twoValues=${true}
							action2='iomo/setMinHeight'
							.selected2=${selectIomoMinHeight}

							.isSatisfied=${() => true}
						>
						</numeric-range>
						<numeric-range
							.title=${{
								de: 'Winkel',
								en: 'Angle',
								fr: 'angle'
							}}
							max="360"
							min="0"
							step="1"
							digits="0"
							unit="°"
							action='iomo/setMaxAngle'
							.selected=${selectIomoMaxAngle}
							.isSatisfied=${() => true}>
						</numeric-range>
					`],
					['cluster', () => html`
						<numeric-range
							.title=${{
								de: 'Höhe',
								en: 'Height',
								fr: 'hauteurs'
							}}
							max="${this.maxHeadHeight}"
							min="${this.minHeadHeight}"
							step="0.1"
							digits="1"
							action='iomo/setMaxHeight'
							.selected=${selectIomoMaxHeight}
							.isSatisfied=${() => true}>
						</numeric-range>
					`],
				]
			)}

		`;
	}
}
